import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import BankAccountCard from './components/BankAccountCard';
import ManualTransactionForm from './components/ManualTransactionForm';
import BankAccountForm from './components/BankAccountForm';
import TransactionFilters from './components/TransactionFilters';
import TransactionTable from './components/TransactionTable';
import ReconciliationPanel from './components/ReconciliationPanel';
import PettyCashPanel from './components/PettyCashPanel';
import PettyCashTransactionForm from './components/PettyCashTransactionForm';
import PettyCashTransactionsTable from './components/PettyCashTransactionsTable';
import ImportHistoryModal from './components/ImportHistoryModal';
import { useAuth } from '../../contexts/AuthContext';
import { bankingService } from '../../services/bankingService';
import TransactionCategoryManager from './components/TransactionCategoryManager';

const Banking = () => {
  const { userProfile, loading: authLoading, hasOrganization, isAuthenticated } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [showBankAccountForm, setShowBankAccountForm] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [activeView, setActiveView] = useState('dashboard'); // dashboard, transactions, reconciliation, bank-feeds, petty-cash
  const [showConnectAccountModal, setShowConnectAccountModal] = useState(false);
  const [showManualAccountForm, setShowManualAccountForm] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);
  const [connectionStep, setConnectionStep] = useState('select'); // select, credentials, verify, complete
  const [showBankStatementModal, setShowBankStatementModal] = useState(false);
  const [showReconciliationReport, setShowReconciliationReport] = useState(false);
  const [selectedTransactionsForReconciliation, setSelectedTransactionsForReconciliation] = useState([]);
  const [bankStatementFile, setBankStatementFile] = useState(null);
  const [importingBankStatement, setImportingBankStatement] = useState(false);
  const [reconciliationInProgress, setReconciliationInProgress] = useState(false);
  const [showImportHistoryModal, setShowImportHistoryModal] = useState(false);

  // Petty Cash state
  const [showPettyCashTransactionForm, setShowPettyCashTransactionForm] = useState(false);
  const [pettyCashTransactionType, setPettyCashTransactionType] = useState('expense');
  const [showPettyCashTransactionsTable, setShowPettyCashTransactionsTable] = useState(false);
  const [pettyCashRefreshTrigger, setPettyCashRefreshTrigger] = useState(0);

  // Add new state for bulk categorization
  const [showBulkCategorizeModal, setShowBulkCategorizeModal] = useState(false);
  const [bulkCategorizeTransactions, setBulkCategorizeTransactions] = useState([]);

  // Add new state for category management
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  // Data state - REMOVED mock data and initialized as empty
  const [bankAccounts, setBankAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [bankingSummary, setBankingSummary] = useState({
    totalBalance: 0,
    totalIncome: 0,
    totalExpenses: 0,
    unreconciledTransactions: 0,
    pendingTransactions: 0,
    accountsCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Available banks configuration (reference data - not mock data)
  const availableBanks = [
    {
      id: 'chase',
      name: 'JPMorgan Chase Bank',
      logo: 'chase-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '3 minutes',
      popular: true,
      isPartner: true,
      directFeed: true,
      description: 'Direct connection with real-time transaction sync',
      features: ['Real-time sync', 'Automatic categorization', 'Multi-account support']
    },
    {
      id: 'bofa',
      name: 'Bank of America',
      logo: 'bofa-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '5 minutes',
      popular: true,
      isPartner: true,
      directFeed: true,
      description: 'Partner bank with enhanced security and daily sync',
      features: ['Daily sync', 'Enhanced security', 'Business account support']
    },
    {
      id: 'wells-fargo',
      name: 'Wells Fargo',
      logo: 'wells-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '4 minutes',
      popular: true,
      isPartner: true,
      directFeed: true,
      description: 'Direct feed with comprehensive business banking support',
      features: ['Business focus', 'Multiple locations', 'Comprehensive reporting']
    },
    {
      id: 'amex',
      name: 'American Express',
      logo: 'amex-logo',
      supportedAccountTypes: ['Credit Card'],
      setupTime: '3 minutes',
      popular: true,
      isPartner: true,
      directFeed: true,
      description: 'Premium credit card integration with detailed categorization',
      features: ['Premium support', 'Detailed categorization', 'Expense insights']
    },
    {
      id: 'citi',
      name: 'Citibank',
      logo: 'citi-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '5 minutes',
      popular: true,
      isPartner: false,
      directFeed: true,
      description: 'Automatic bank feeds with standard sync frequency',
      features: ['Standard sync', 'Multi-currency', 'International support']
    },
    {
      id: 'capital-one',
      name: 'Capital One',
      logo: 'capital-one-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '3 minutes',
      popular: true,
      isPartner: false,
      directFeed: true,  
      description: 'Digital-first banking with modern API integration',
      features: ['Digital focus', 'API integration', 'Modern interface']
    },
    {
      id: 'td-bank',
      name: 'TD Bank',
      logo: 'td-logo',
      supportedAccountTypes: ['Checking', 'Savings'],
      setupTime: '5 minutes',
      popular: false,
      isPartner: false,
      directFeed: true,
      description: 'Regional bank with reliable feed connection',
      features: ['Regional focus', 'Reliable sync', 'Personal service']
    },
    {
      id: 'pnc',
      name: 'PNC Bank',  
      logo: 'pnc-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '5 minutes',
      popular: false,
      isPartner: false,
      directFeed: true,
      description: 'Business banking specialist with comprehensive features',
      features: ['Business specialist', 'Comprehensive features', 'Regional presence']
    },
    {
      id: 'usaa',
      name: 'USAA',
      logo: 'usaa-logo',
      supportedAccountTypes: ['Checking', 'Savings', 'Credit Card'],
      setupTime: '5 minutes',
      popular: false,
      isPartner: false,
      directFeed: true,
      description: 'Military-focused banking with secure connections',
      features: ['Military focus', 'High security', 'Member benefits']
    }
  ];

  // Load banking data from Supabase with enhanced error handling
  useEffect(() => {
    if (isAuthenticated && hasOrganization && !authLoading) {
      loadBankingData();
    }
  }, [isAuthenticated, hasOrganization, authLoading]);

  const loadBankingData = async () => {
    try {
      setLoading(true);
      setError('');

      // Ensure authentication is fully initialized before making API calls
      if (!isAuthenticated) {
        setError('Please sign in to access banking data');
        setLoading(false);
        return;
      }

      if (!hasOrganization) {
        setError('You need to be a member of an organization to access banking features');
        setLoading(false);
        return;
      }

      // Add a small delay to ensure authentication state is stable
      await new Promise(resolve => setTimeout(resolve, 100));

      // Load bank accounts and summary in parallel
      const [accountsData, summaryData] = await Promise.all([
        bankingService?.getAllBankAccounts(),
        bankingService?.getBankingSummary()
      ]);

      setBankAccounts(accountsData);
      setBankingSummary(summaryData);

      // Load transactions if there are accounts
      if (accountsData?.length > 0) {
        const transactionsData = await bankingService?.getBankAccountTransactions(selectedAccount, filters);
        setTransactions(transactionsData);
      }

    } catch (err) {
      console.error('Error loading banking data:', err);
      let errorMessage = err?.message || 'Failed to load banking data';
      
      // Provide more specific error messages
      if (errorMessage?.includes('Not authenticated')) {
        errorMessage = 'Authentication session expired. Please refresh the page or sign in again.';
      } else if (errorMessage?.includes('organization')) {
        errorMessage = 'Unable to access organization data. Please ensure you are a member of an organization.';
      } else if (errorMessage?.includes('Failed to fetch')) {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load transactions when filters or selected account changes
  // Enhanced with authentication checks
  useEffect(() => {
    if (isAuthenticated && hasOrganization && bankAccounts?.length > 0 && !authLoading) {
      loadTransactions();
    }
  }, [selectedAccount, filters, isAuthenticated, hasOrganization, authLoading]);

  const loadTransactions = async () => {
    try {
      // Ensure authentication is valid before making API calls
      if (!isAuthenticated || !hasOrganization) {
        return;
      }

      const transactionsData = await bankingService?.getBankAccountTransactions(selectedAccount, filters);
      setTransactions(transactionsData);
    } catch (err) {
      console.error('Error loading transactions:', err);
      let errorMessage = err?.message || 'Failed to load transactions';
      
      if (errorMessage?.includes('Not authenticated')) {
        errorMessage = 'Authentication session expired. Please refresh the page or sign in again.';
      }
      
      setError(errorMessage);
    }
  };

  // Initialize empty reconciliation summary
  const reconciliationSummary = {
    accountName: selectedAccount === 'all' ? 'All Accounts' : bankAccounts?.find(acc => acc?.id === selectedAccount)?.accountName || 'No Account Selected',
    bankBalance: bankingSummary?.totalBalance || 0,
    bookBalance: bankingSummary?.totalBalance || 0,
    outstandingDeposits: 0,
    outstandingWithdrawals: 0,
    variance: 0,
    lastReconciled: null,
    reconciledCount: transactions?.filter(t => t?.reconciliationStatus === 'reconciled')?.length || 0,
    unreconciledCount: bankingSummary?.unreconciledTransactions || 0,
    expectedInflow: 0,
    expectedOutflow: 0,
    projectedBalance: bankingSummary?.totalBalance || 0,
    reconciliationStatus: 'pending',
    nextReconciliationDue: null
  };

  // Filter transactions based on current filters
  const filteredTransactions = transactions?.filter(transaction => {
    if (selectedAccount !== 'all' && transaction?.bankAccountId !== selectedAccount) return false;
    if (filters?.account && filters?.account !== 'all' && transaction?.bankAccountId !== filters?.account) return false;
    if (filters?.type && filters?.type !== 'all') {
      const transactionType = transaction?.amount > 0 ? 'income' : 'expense';
      if (transactionType !== filters?.type) return false;
    }
    if (filters?.status && filters?.status !== 'all' && transaction?.reconciliationStatus !== filters?.status) return false;
    if (filters?.category && filters?.category !== 'all' && transaction?.category !== filters?.category) return false;
    if (filters?.search && !transaction?.description?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    if (filters?.dateFrom && transaction?.transactionDate < filters?.dateFrom) return false;
    if (filters?.dateTo && transaction?.transactionDate > filters?.dateTo) return false;
    if (filters?.amountFrom && Math.abs(transaction?.amount) < parseFloat(filters?.amountFrom)) return false;
    if (filters?.amountTo && Math.abs(transaction?.amount) > parseFloat(filters?.amountTo)) return false;
    
    return true;
  }) || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount || 0);
  };

  const handleSyncAccount = async (accountId) => {
    try {
      // Implement actual bank sync functionality
      console.log('Syncing account:', accountId);
      
      // Update account sync status to show it's in progress
      setBankAccounts(prev => prev?.map(account => 
        account?.id === accountId 
          ? { ...account, feedStatus: 'pending', lastSyncAt: new Date()?.toISOString() }
          : account
      ));

      // In a real implementation, this would trigger a bank feed sync
      // For now, we'll simulate a successful sync by updating the account status
      await bankingService?.updateBankAccount(accountId, {
        feedStatus: 'active',
        lastSyncAt: new Date()?.toISOString()
      });

      // Refresh banking data after sync
      await loadBankingData(); 
      
      console.log('Account sync completed successfully');
    } catch (err) {
      console.error('Error syncing account:', err);
      setError(err?.message || 'Failed to sync account');
      
      // Revert account status on error
      setBankAccounts(prev => prev?.map(account => 
        account?.id === accountId 
          ? { ...account, feedStatus: 'error' }
          : account
      ));
    }
  };

  const handleViewAccountDetails = (accountId) => {
    console.log('Viewing account details:', accountId);
    setSelectedAccount(accountId);
    setActiveView('transactions');
  };

  const handleConnectAccount = () => {
    setShowConnectAccountModal(true);
    setConnectionStep('select');
  };

  // Enhanced categorization handler
  const handleCategorizeTransaction = async (transactionId, category) => {
    try {
      // Update transaction category in database
      await bankingService?.updateTransaction(transactionId, { category });
      
      // Update local state to reflect the change
      setTransactions(prev => prev?.map(transaction => 
        transaction?.id === transactionId 
          ? { ...transaction, category, aiSuggested: false }
          : transaction
      ));
      
      console.log('Transaction categorized successfully');
    } catch (err) {
      console.error('Error categorizing transaction:', err);
      setError(err?.message || 'Failed to categorize transaction');
    }
  };

  // New bulk categorize handler
  const handleBulkCategorize = async (category) => {
    try {
      // Update multiple transactions with the same category
      const updatePromises = bulkCategorizeTransactions?.map(transactionId => 
        bankingService?.updateTransaction(transactionId, { category })
      );
      
      await Promise.all(updatePromises);
      
      // Update local state for all selected transactions
      setTransactions(prev => prev?.map(transaction => 
        bulkCategorizeTransactions?.includes(transaction?.id)
          ? { ...transaction, category, aiSuggested: false }
          : transaction
      ));
      
      setShowBulkCategorizeModal(false);
      setBulkCategorizeTransactions([]);
      
      console.log(`Successfully categorized ${bulkCategorizeTransactions?.length} transactions as ${category}`);
    } catch (err) {
      console.error('Error bulk categorizing transactions:', err);
      setError(err?.message || 'Failed to bulk categorize transactions');
    }
  };

  const handleReconcileTransaction = async (transactionId) => {
    try {
      await bankingService?.updateTransactionReconciliation(transactionId, 'reconciled');
      await loadTransactions();
      await loadBankingData(); // Refresh summary
    } catch (err) {
      setError(err?.message || 'Failed to reconcile transaction');
    }
  };

  // Enhanced bulk action handler to support AI categorization
  const handleBulkAction = async (action, transactionIds) => {
    try {
      console.log(`Bulk ${action} for transactions:`, transactionIds);
      
      if (action === 'categorize') {
        // Show bulk categorization modal/dropdown
        setBulkCategorizeTransactions(transactionIds);
        setShowBulkCategorizeModal(true);
      } else if (action === 'auto-categorize') {
        // Use AI to auto-categorize selected transactions
        setLoading(true);
        const result = await bankingService?.bulkAutoCategorizeTransactions(transactionIds);
        await loadTransactions();
        await loadBankingData(); // Refresh summary
        
        // Show results notification
        let message = `AI categorized ${result?.successfulCount} out of ${result?.totalProcessed} transactions`;
        console.log(message);
        
        // You could show a toast notification here
        alert(message);
        
      } else if (action === 'reconcile') {
        await bankingService?.bulkUpdateTransactionReconciliation(transactionIds, 'reconciled');
        await loadTransactions();
        await loadBankingData(); // Refresh summary
      }
    } catch (err) {
      setError(err?.message || `Failed to ${action} transactions`);
    } finally {
      setLoading(false);
    }
  };

  // Add new handler for transaction updates
  const handleTransactionUpdate = async (updatedTransaction, action = 'update', transactionId = null) => {
    try {
      // Refresh transactions to get latest data
      await loadTransactions();
      
      // Show success message based on action
      let message = '';
      switch (action) {
        case 'update':
          message = 'Transaction updated successfully';
          break;
        case 'create':
          message = 'Transaction created successfully';
          break;
        case 'delete':
          message = 'Transaction deleted successfully';
          break;
        default:
          message = 'Transaction updated successfully';
      }
      
      // You could show a toast notification here if you have a toast system
      console.log(message, updatedTransaction);
      
    } catch (error) {
      console.error('Error refreshing transactions:', error);
      // You could show an error toast here
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({});
  };

  const handleAddManualTransaction = async (transactionData) => {
    try {
      await bankingService?.createTransaction(transactionData);
      await loadTransactions();
      await loadBankingData(); // Refresh summary
      setShowManualForm(false);
    } catch (err) {
      setError(err?.message || 'Failed to add transaction');
    }
  };

  const handleStartReconciliation = () => {
    setActiveView('reconciliation');
  };

  const handleViewReconciliationReport = () => {
    setShowReconciliationReport(true);
  };

  const handleConnectBankAccount = (bankId) => {
    const bank = availableBanks?.find(b => b?.id === bankId);
    if (bank) {
      setSelectedBank(bank);
      setConnectionStep('credentials');
      setShowConnectAccountModal(true);
    }
  };

  const handleManualAccountAdd = () => {
    setShowBankAccountForm(true);
    setShowConnectAccountModal(false);
  };

  const handleAddBankAccount = async (accountData) => {
    try {
      await bankingService?.createBankAccount(accountData);
      await loadBankingData();
      setShowBankAccountForm(false);
    } catch (err) {
      setError(err?.message || 'Failed to add bank account');
    }
  };

  // Enhanced connection status check
  const checkConnectionStatus = (account) => {
    if (!account?.feedEnabled || account?.feedStatus === 'inactive') {
      return 'not_connected';
    }
    if (account?.feedStatus === 'pending') {
      return 'connecting';
    }
    if (account?.feedStatus === 'active' && account?.lastSyncAt) {
      return 'connected';
    }
    return 'error';
  };

  // Updated connection completion handler  
  const handleConnectionComplete = async () => {
    setConnectionStep('complete');
    
    try {
      // Update the selected bank account to show it's connected
      if (selectedBank && selectedAccount !== 'all') {
        await bankingService?.updateBankAccount(selectedAccount, {
          feedEnabled: true,
          feedStatus: 'active',
          lastSyncAt: new Date()?.toISOString()
        });
        
        // Refresh banking data
        await loadBankingData();
      }
    } catch (err) {
      setError('Failed to complete bank connection: ' + err?.message);
    }
    
    // Show success message briefly
    setTimeout(() => {
      setShowConnectAccountModal(false);
      setConnectionStep('select');
      setSelectedBank(null);
    }, 2000);
  };

  // Enhanced connection flow for existing accounts
  const handleReconnectAccount = async (accountId) => {
    try {
      const account = bankAccounts?.find(acc => acc?.id === accountId);
      if (account) {
        setSelectedAccount(accountId);
        setSelectedBank({
          id: account?.bankName?.toLowerCase()?.replace(/\s+/g, '-'),
          name: account?.bankName,
          description: `Reconnect to ${account?.bankName}`,
          features: ['Reconnect existing account', 'Resume transaction sync']
        });
        setConnectionStep('credentials');
        setShowConnectAccountModal(true);
      }
    } catch (err) {
      setError('Failed to reconnect account: ' + err?.message);
    }
  };

  const handleImportBankStatement = () => {
    setShowBankStatementModal(true);
  };

  const handleBankStatementUpload = async (file, bankAccountId) => {
    if (!file || !bankAccountId) {
      setError('Please select a file and bank account');
      return;
    }

    setImportingBankStatement(true);

    try {
      // Upload and process the bank statement
      const importResult = await bankingService?.uploadBankStatement(file, bankAccountId);
      
      console.log('Bank statement import started:', importResult);
      
      // Show success message
      setError(''); // Clear any previous errors
      alert(`Bank statement "${file?.name}" uploaded successfully! Processing has started and transactions will appear shortly.`);
      
      // Refresh banking data to show any immediately processed transactions
      await loadBankingData();
      
      setShowBankStatementModal(false);
      setBankStatementFile(null);
      
    } catch (error) {
      console.error('Error importing bank statement:', error);
      setError(error?.message || 'Failed to import bank statement');
    } finally {
      setImportingBankStatement(false);
    }
  };

  const handleMarkAsReconciled = async () => {
    try {
      setReconciliationInProgress(true);
      
      const unreconciledTransactions = transactions?.filter(t => 
        t?.reconciliationStatus === 'unreconciled' || t?.reconciliationStatus === 'pending_reconciliation'
      );
      
      if (unreconciledTransactions?.length === 0) {
        console.log('No transactions to reconcile');
        setReconciliationInProgress(false);
        return;
      }

      const transactionIds = unreconciledTransactions?.map(t => t?.id);
      await bankingService?.bulkUpdateTransactionReconciliation(transactionIds, 'reconciled');
      
      await loadTransactions();
      await loadBankingData(); // Refresh summary
      
      console.log(`Successfully reconciled ${unreconciledTransactions?.length} transactions`);
    } catch (err) {
      setError(err?.message || 'Failed to reconcile transactions');
    } finally {
      setReconciliationInProgress(false);
    }
  };

  const handleExportReconciliationReport = () => {
    const reportData = {
      reportDate: new Date()?.toISOString()?.split('T')?.[0],
      accountName: reconciliationSummary?.accountName || 'All Accounts',
      bankBalance: reconciliationSummary?.bankBalance || 0,
      bookBalance: reconciliationSummary?.bookBalance || 0,
      variance: reconciliationSummary?.variance || 0,
      reconciledTransactions: transactions?.filter(t => t?.reconciliationStatus === 'reconciled')?.length || 0,
      unreconciledTransactions: transactions?.filter(t => t?.reconciliationStatus === 'unreconciled')?.length || 0,
      transactions: transactions?.map(t => ({
        date: t?.transactionDate,
        description: t?.description,
        amount: t?.amount,
        status: t?.reconciliationStatus,
        category: t?.category
      }))
    };

    // Create CSV content
    const csvContent = [
      'Bank Reconciliation Report',
      `Account: ${reportData?.accountName}`,
      `Report Date: ${reportData?.reportDate}`,
      `Bank Balance: ${reportData?.bankBalance?.toLocaleString()}`,
      `Book Balance: ${reportData?.bookBalance?.toLocaleString()}`,
      `Variance: ${reportData?.variance?.toLocaleString()}`,
      '',
      'Transaction Details',
      'Date,Description,Amount,Status,Category',
      ...reportData?.transactions?.map(t => 
        `${t?.date},"${t?.description}",${t?.amount},${t?.status},${t?.category}`
      )
    ]?.join('\n');

    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `reconciliation-report-${reportData?.reportDate}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    console.log('Reconciliation report exported successfully');
  };

  // Add this function - handleExportTransactions
  const handleExportTransactions = () => {
    const exportData = {
      exportDate: new Date()?.toISOString()?.split('T')?.[0],
      accountName: selectedAccount === 'all' ? 'All Accounts' : bankAccounts?.find(acc => acc?.id === selectedAccount)?.accountName || 'Unknown',
      transactions: filteredTransactions?.map(t => ({
        date: t?.transactionDate,
        description: t?.description,
        amount: t?.amount,
        type: t?.amount > 0 ? 'Income' : 'Expense',
        category: t?.category,
        status: t?.reconciliationStatus,
        bankAccount: bankAccounts?.find(acc => acc?.id === t?.bankAccountId)?.accountName || 'Unknown'
      }))
    };

    // Create CSV content
    const csvContent = [
      'Transaction Export',
      `Account: ${exportData?.accountName}`,
      `Export Date: ${exportData?.exportDate}`,
      `Total Transactions: ${exportData?.transactions?.length}`,
      '',
      'Date,Description,Amount,Type,Category,Status,Bank Account',
      ...exportData?.transactions?.map(t => 
        `${t?.date},"${t?.description}",${t?.amount},${t?.type},${t?.category},${t?.status},"${t?.bankAccount}"`
      )
    ]?.join('\n');

    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link?.setAttribute('href', url);
    link?.setAttribute('download', `transactions-export-${exportData?.exportDate}.csv`);
    link.style.visibility = 'hidden';
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    console.log('Transactions exported successfully');
  };

  // Petty Cash handlers
  const handleAddPettyCashExpense = () => {
    setPettyCashTransactionType('expense');
    setShowPettyCashTransactionForm(true);
  };

  const handleAddPettyCashReplenishment = () => {
    setPettyCashTransactionType('replenishment');
    setShowPettyCashTransactionForm(true);
  };

  const handleViewPettyCashTransactions = () => {
    setShowPettyCashTransactionsTable(true);
  };

  const handlePettyCashTransactionSubmit = (result) => {
    // Refresh the petty cash panel
    setPettyCashRefreshTrigger(prev => prev + 1);
    console.log('Petty cash transaction created successfully:', result);
  };

  // Bank Feeds View
  const renderBankFeedsView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Bank Feeds</h2>
          <p className="text-muted-foreground">Connect and manage automatic bank feeds</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="History"
            iconPosition="left"
            onClick={() => setShowImportHistoryModal(true)}
          >
            Import History
          </Button>
          <Button
            variant="outline"
            iconName="Upload"
            iconPosition="left"
            onClick={handleImportBankStatement}
          >
            Import Statement
          </Button>
          <Button
            variant="default"
            iconName="Link"
            iconPosition="left"
            onClick={handleConnectAccount}
          >
            Connect Bank
          </Button>
        </div>
      </div>

      {/* Connected Banks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {availableBanks?.slice(0, 6)?.map(bank => (
          <div key={bank?.id} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-primary font-semibold text-sm">
                    {bank?.name?.split(' ')?.map(word => word?.[0])?.join('')?.slice(0, 2)}
                  </span>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground">{bank?.name}</h3>
                  <p className="text-xs text-muted-foreground">{bank?.setupTime} setup</p>
                </div>
              </div>
              {bank?.popular && (
                <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">Popular</span>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-4">{bank?.description}</p>
            <div className="flex flex-wrap gap-1 mb-4">
              {bank?.features?.slice(0, 2)?.map(feature => (
                <span key={feature} className="px-2 py-1 bg-muted text-xs rounded">
                  {feature}
                </span>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => handleConnectBankAccount(bank?.id)}
            >
              Connect
            </Button>
          </div>
        ))}
      </div>
    </div>
  );

  // Transactions View - Enhanced with category management
  const renderTransactionsView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Transactions</h2>
          <p className="text-muted-foreground">View and manage all your transactions</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="Settings"
            iconPosition="left"
            onClick={() => setShowCategoryManager(true)}
          >
            Manage Categories
          </Button>
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={handleExportTransactions}
            disabled={filteredTransactions?.length === 0}
          >
            Export
          </Button>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowManualForm(true)}
          >
            Add Transaction
          </Button>
        </div>
      </div>

      <TransactionFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        bankAccounts={bankAccounts}
        selectedAccount={selectedAccount}
        onAccountChange={setSelectedAccount}
      />

      <TransactionTable
        transactions={transactions}
        onCategorize={handleCategorizeTransaction}
        onReconcile={handleReconcileTransaction}
        onBulkAction={handleBulkAction}
        onTransactionUpdate={handleTransactionUpdate}
      />
    </div>
  );

  // Reconciliation View
  const renderReconciliationView = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Reconciliation</h2>
          <p className="text-muted-foreground">Match your records with bank statements</p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
            onClick={handleExportReconciliationReport}
          >
            Export Report
          </Button>
          <Button
            variant="default"
            iconName="CheckCircle"
            iconPosition="left"
            onClick={handleMarkAsReconciled}
            disabled={reconciliationInProgress}
          >
            {reconciliationInProgress ? 'Processing...' : 'Mark as Reconciled'}
          </Button>
        </div>
      </div>

      <ReconciliationPanel
        summary={reconciliationSummary}
        transactions={filteredTransactions}
        onReconcile={handleReconcileTransaction}
        onBulkReconcile={(transactionIds) => handleBulkAction('reconcile', transactionIds)}
        onStartReconciliation={handleStartReconciliation}
        onViewReport={handleViewReconciliationReport}
      />
    </div>
  );

  // Enhanced petty cash view that handles organization state
  const renderPettyCashView = () => {
    // If user doesn't have organization, show setup message
    if (isAuthenticated && !hasOrganization) {
      return (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-warning text-2xl">🏢</span>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Organization Required</h3>
            <p className="text-muted-foreground mb-4">
              You need to be a member of an organization to manage petty cash transactions.
            </p>
            <div className="flex justify-center space-x-3">
              <Button
                variant="default"
                onClick={() => window.location.href = '/organization-settings'}
                iconName="Settings"
                iconPosition="left"
              >
                Setup Organization
              </Button>
              <Button
                variant="outline"
                onClick={() => setActiveView('dashboard')}
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Show petty cash panel (will handle its own loading/error states)
    return (
      <PettyCashPanel
        onAddExpense={handleAddPettyCashExpense}
        onAddReplenishment={handleAddPettyCashReplenishment}
        onViewTransactions={handleViewPettyCashTransactions}
        key={pettyCashRefreshTrigger} // Force re-render when refreshTrigger changes
      />
    );
  };

  // Enhanced banking dashboard with organization awareness
  const renderBankingDashboard = () => (
    <>
      {/* Connection Status Alert */}
      {bankAccounts?.length > 0 && bankAccounts?.some(acc => !acc?.feedEnabled) && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center">
                <span className="text-warning text-lg">⚠️</span>
              </div>
              <div>
                <h4 className="font-medium text-warning">Bank Accounts Not Connected</h4>
                <p className="text-warning/80 text-sm">
                  {bankAccounts?.filter(acc => !acc?.feedEnabled)?.length} bank account(s) need to be connected to enable automatic transaction sync.
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const unconnectedAccount = bankAccounts?.find(acc => !acc?.feedEnabled);
                if (unconnectedAccount) {
                  handleReconnectAccount(unconnectedAccount?.id);
                }
              }}
              className="border-warning text-warning hover:bg-warning hover:text-warning-foreground"
            >
              Connect Now
            </Button>
          </div>
        </div>
      )}

      {/* Show organization status if there are issues */}
      {isAuthenticated && !hasOrganization && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-warning/20 rounded-lg flex items-center justify-center">
              <span className="text-warning text-lg">🏢</span>
            </div>
            <div>
              <h4 className="font-medium text-warning">Limited Functionality</h4>
              <p className="text-warning/80 text-sm">
                Some features require organization membership. <button 
                  onClick={() => window.location.href = '/organization-settings'} 
                  className="underline hover:no-underline"
                >
                  Set up your organization
                </button> to access all banking features.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Show error message if any */}
      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-error/20 rounded-lg flex items-center justify-center">
              <span className="text-error text-lg">⚠️</span>
            </div>
            <div>
              <h4 className="font-medium text-error">Error</h4>
              <p className="text-error/80 text-sm">{error}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError('')}
              iconName="X"
            />
          </div>
        </div>
      )}

      {/* Banking Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground">Total Balance</h3>
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <span className="text-blue-600 text-lg">💰</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatCurrency(bankingSummary?.totalBalance)}</p>
          <p className="text-sm text-success mt-1">Across {bankingSummary?.accountsCount} accounts</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground">Monthly Income</h3>
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-600 text-lg">📈</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-success">{formatCurrency(bankingSummary?.totalIncome)}</p>
          <p className="text-sm text-muted-foreground mt-1">This month</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground">Monthly Expenses</h3>
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <span className="text-red-600 text-lg">📉</span>
            </div>
          </div>
          <p className="text-2xl font-bold text-error">{formatCurrency(bankingSummary?.totalExpenses)}</p>
          <p className="text-sm text-muted-foreground mt-1">This month</p>
        </div>

        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-muted-foreground">Connection Status</h3>
            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
              <span className="text-orange-600 text-lg">🔗</span>
            </div>
          </div>
          <div className="space-y-1">
            <p className="text-lg font-bold text-success">
              {bankAccounts?.filter(acc => acc?.feedEnabled)?.length} Connected
            </p>
            <p className="text-sm text-warning">
              {bankAccounts?.filter(acc => !acc?.feedEnabled)?.length} Not Connected
            </p>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading banking data...</p>
          </div>
        </div>  
      )}

      {/* No Data State for Bank Accounts */}
      {!loading && bankAccounts?.length === 0 ? (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">Bank Accounts</h2>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowBankAccountForm(true)}
              >
                Add Account
              </Button>
              <Button
                variant="outline"
                iconName="Link"
                iconPosition="left"
                onClick={handleConnectAccount}
              >
                Connect Account
              </Button>
            </div>
          </div>
          <div className="bg-card border border-border rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-blue-600 text-2xl">🏦</span>
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Bank Accounts Connected</h3>
            <p className="text-muted-foreground mb-6">
              Connect your bank accounts to start managing transactions and reconciliation.
            </p>
            <div className="flex justify-center space-x-3">
              <Button
                variant="default"
                iconName="Link"
                iconPosition="left"
                onClick={handleConnectAccount}
              >
                Connect Bank Account
              </Button>
              <Button
                variant="outline"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowBankAccountForm(true)}
              >
                Add Manually
              </Button>
            </div>
          </div>
        </div>
      ) : (
        !loading && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-foreground">Bank Accounts</h2>
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowBankAccountForm(true)}
                >
                  Add Account
                </Button>
                <Button
                  variant="outline"
                  iconName="Link"
                  iconPosition="left"
                  onClick={handleConnectAccount}
                >
                  Connect Account
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {bankAccounts?.map(account => (
                <BankAccountCard
                  key={account?.id}
                  account={{
                    ...account,
                    connectionStatus: checkConnectionStatus(account),
                    syncStatus: account?.feedEnabled ? 
                      (account?.feedStatus === 'active' ? 'synced' : 'pending') : 
                      'not_connected'
                  }}
                  onSync={handleSyncAccount}
                  onViewDetails={handleViewAccountDetails}
                  onReconnect={handleReconnectAccount}
                />
              ))}
            </div>
          </div>
        )
      )}

      {/* Quick Actions & Recent Activity */}
      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowManualForm(true)}
              >
                Record Transaction
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => loadBankingData()}
                disabled={bankAccounts?.length === 0}
              >
                Sync All Accounts
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                iconName="Link"
                iconPosition="left"
                onClick={handleConnectAccount}
                disabled={bankAccounts?.length === 0}
              >
                Connect More Banks
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start" 
                iconName="CheckCircle"
                iconPosition="left"
                onClick={handleStartReconciliation}
                disabled={transactions?.length === 0}
              >
                Start Reconciliation
              </Button>
            </div>
          </div>

          {/* Recent Transactions Preview */}
          <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Recent Transactions</h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setActiveView('transactions')}
                disabled={transactions?.length === 0}
              >
                View All
              </Button>
            </div>
            {transactions?.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-muted-foreground text-lg">📝</span>
                </div>
                <h4 className="text-base font-medium text-foreground mb-2">No Transactions Yet</h4>
                <p className="text-muted-foreground text-sm mb-4">
                  Start by connecting a bank account or recording manual transactions.
                </p>
                <Button 
                  variant="outline" 
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowManualForm(true)}
                >
                  Record Transaction
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions?.slice(0, 5)?.map(transaction => (
                  <div key={transaction?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        transaction?.amount > 0 ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                      }`}>
                        {transaction?.amount > 0 ? '↓' : '↑'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{transaction?.description}</p>
                        <p className="text-xs text-muted-foreground">{transaction?.transactionDate}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-semibold ${
                        transaction?.amount > 0 ? 'text-success' : 'text-error'
                      }`}>
                        {transaction?.amount > 0 ? '+' : ''}{formatCurrency(transaction?.amount)}
                      </p>
                      <p className="text-xs text-muted-foreground">{transaction?.category}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );

  // Show loading state while authentication is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar 
          isCollapsed={sidebarCollapsed} 
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading banking data...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          {/* Page Header with Navigation */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Banking</h1>
              <p className="text-muted-foreground mt-2">
                Manage bank accounts, transactions, and reconciliation
              </p>
              {/* Show organization status */}
              {isAuthenticated && userProfile && (
                <div className="flex items-center mt-2 text-sm">
                  {hasOrganization ? (
                    <>
                      <span className="w-2 h-2 bg-success rounded-full mr-2"></span>
                      <span className="text-success">Organization: {userProfile?.organizations?.[0]?.name || 'Connected'}</span>
                    </>
                  ) : (
                    <>
                      <span className="w-2 h-2 bg-warning rounded-full mr-2"></span>
                      <span className="text-warning">No organization - Limited functionality</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                iconName="RefreshCw"
                iconPosition="left"
                onClick={() => loadBankingData()}
                disabled={loading || bankAccounts?.length === 0}
              >
                Sync All
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowManualForm(true)}
              >
                Add Transaction
              </Button>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center space-x-1 mb-8 bg-muted/30 p-1 rounded-lg w-fit">
            <Button
              variant={activeView === 'dashboard' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('dashboard')}
            >
              Dashboard
            </Button>
            <Button
              variant={activeView === 'bank-feeds' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('bank-feeds')}
            >
              Bank Feeds
            </Button>
            <Button
              variant={activeView === 'transactions' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('transactions')}
            >
              Transactions
            </Button>
            <Button
              variant={activeView === 'reconciliation' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('reconciliation')}
            >
              Reconciliation
            </Button>
            <Button
              variant={activeView === 'petty-cash' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setActiveView('petty-cash')}
            >
              Petty Cash
            </Button>
          </div>

          {/* Dynamic Content Based on Active View */}
          {activeView === 'dashboard' && renderBankingDashboard()}
          {activeView === 'bank-feeds' && renderBankFeedsView()}
          {activeView === 'transactions' && renderTransactionsView()}
          {activeView === 'reconciliation' && renderReconciliationView()}
          {activeView === 'petty-cash' && renderPettyCashView()}
        </div>
      </main>
      {/* Manual Transaction Form Modal */}
      <ManualTransactionForm
        isOpen={showManualForm}
        onClose={() => setShowManualForm(false)}
        onSubmit={handleAddManualTransaction}
        bankAccounts={bankAccounts}
      />
      {/* Bank Account Form Modal */}
      <BankAccountForm
        isOpen={showBankAccountForm}
        onClose={() => setShowBankAccountForm(false)}
        onSubmit={handleAddBankAccount}
      />
      {/* Connect Account Modal */}
      {showConnectAccountModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Connect Bank Account</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose a bank to connect for automatic transaction sync
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowConnectAccountModal(false)}
                iconName="X"
              />
            </div>

            <div className="p-6">
              {connectionStep === 'select' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {availableBanks?.map(bank => (
                      <div key={bank?.id} className="border border-border rounded-lg p-4 hover:bg-muted/20 cursor-pointer transition-colors"
                           onClick={() => handleConnectBankAccount(bank?.id)}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                              <span className="text-primary font-semibold text-sm">
                                {bank?.name?.split(' ')?.map(word => word?.[0])?.join('')?.slice(0, 2)}
                              </span>
                            </div>
                            <div>
                              <h3 className="text-sm font-semibold text-foreground">{bank?.name}</h3>
                              <p className="text-xs text-muted-foreground">{bank?.setupTime}</p>
                            </div>
                          </div>
                          {bank?.popular && (
                            <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">Popular</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-3">{bank?.description}</p>
                        <div className="flex flex-wrap gap-1">
                          {bank?.features?.slice(0, 3)?.map(feature => (
                            <span key={feature} className="px-2 py-1 bg-muted text-xs rounded">
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="border-t border-border pt-6">
                    <Button
                      variant="outline"
                      onClick={handleManualAccountAdd}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      Add Account Manually
                    </Button>
                  </div>
                </div>
              )}

              {connectionStep === 'credentials' && selectedBank && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-primary text-2xl">🔗</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Connecting to {selectedBank?.name}</h3>
                  <p className="text-muted-foreground mb-6">
                    You'll be redirected to {selectedBank?.name} to securely authorize the connection.
                  </p>
                  <div className="flex justify-center space-x-3">
                    <Button
                      variant="outline"
                      onClick={() => setConnectionStep('select')}
                    >
                      Back
                    </Button>
                    <Button
                      variant="default"
                      onClick={handleConnectionComplete}
                    >
                      Continue to {selectedBank?.name}
                    </Button>
                  </div>
                </div>
              )}

              {connectionStep === 'complete' && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-success text-2xl">✅</span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Connection Successful!</h3>
                  <p className="text-muted-foreground">
                    Your {selectedBank?.name} account has been connected successfully.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Bank Statement Import Modal */}
      {showBankStatementModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Import Bank Statement</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload your bank statement to automatically import transactions
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowBankStatementModal(false);
                  setBankStatementFile(null);
                  setError('');
                }}
                iconName="X"
              />
            </div>

            <div className="p-6 space-y-6">
              {/* Error Display */}
              {error && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-error/20 rounded-lg flex items-center justify-center">
                      <span className="text-error text-lg">⚠️</span>
                    </div>
                    <div>
                      <h4 className="font-medium text-error">Import Error</h4>
                      <p className="text-error/80 text-sm">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Bank Account Selection */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Select Bank Account *
                </label>
                <select 
                  className="w-full p-3 border border-border rounded-lg bg-background text-foreground"
                  value={selectedAccount === 'all' ? '' : selectedAccount}
                  onChange={(e) => setSelectedAccount(e?.target?.value)}
                  disabled={importingBankStatement}
                >
                  <option value="">Choose a bank account...</option>
                  {bankAccounts?.map(account => (
                    <option key={account?.id} value={account?.id}>
                      {account?.accountName} ({account?.bankName})
                    </option>
                  ))}
                </select>
              </div>

              {/* File Upload Area */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Upload Statement File *
                </label>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-muted/10 hover:bg-muted/20 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.pdf,.qif,.ofx"
                    onChange={(e) => setBankStatementFile(e?.target?.files?.[0])}
                    className="hidden"
                    id="statement-file-upload"
                    disabled={importingBankStatement}
                  />
                  <label 
                    htmlFor="statement-file-upload" 
                    className="cursor-pointer block"
                  >
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-primary text-2xl">📄</span>
                    </div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {bankStatementFile ? bankStatementFile?.name : 'Choose a file to upload'}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supported formats: CSV, Excel (.xlsx, .xls), PDF, QIF, OFX
                    </p>
                    <p className="text-muted-foreground text-xs mt-2">
                      Maximum file size: 10MB
                    </p>
                  </label>
                </div>
              </div>

              {/* File Info */}
              {bankStatementFile && (
                <div className="bg-muted/20 border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-foreground">Selected File:</h4>
                      <p className="text-sm text-muted-foreground">{bankStatementFile?.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Size: {(bankStatementFile?.size / 1024 / 1024)?.toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setBankStatementFile(null)}
                      iconName="X"
                      disabled={importingBankStatement}
                    />
                  </div>
                </div>
              )}

              {/* Supported Format Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">📋 Import Guidelines</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• <strong>CSV files</strong>: Should contain Date, Description, and Amount columns</li>
                  <li>• <strong>Excel files</strong>: First row should contain column headers</li>
                  <li>• <strong>PDF files</strong>: Will be processed for manual review</li>
                  <li>• <strong>QIF/OFX</strong>: Standard banking formats supported</li>
                  <li>• Transactions will be imported as "unreconciled" for your review</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBankStatementModal(false);
                    setBankStatementFile(null);
                    setError('');
                  }}
                  disabled={importingBankStatement}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={() => handleBankStatementUpload(bankStatementFile, selectedAccount)}
                  disabled={!bankStatementFile || !selectedAccount || selectedAccount === 'all' || importingBankStatement}
                  iconName={importingBankStatement ? "RefreshCw" : "Upload"}
                  iconPosition="left"
                  className={importingBankStatement ? "animate-spin" : ""}
                >
                  {importingBankStatement ? 'Processing...' : 'Import Statement'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Petty Cash Transaction Form Modal */}
      <PettyCashTransactionForm
        isOpen={showPettyCashTransactionForm}
        onClose={() => setShowPettyCashTransactionForm(false)}
        onSubmit={handlePettyCashTransactionSubmit}
        transactionType={pettyCashTransactionType}
      />
      {/* Petty Cash Transactions Table Modal */}
      {showPettyCashTransactionsTable && (
        <PettyCashTransactionsTable
          onClose={() => setShowPettyCashTransactionsTable(false)}
          refreshTrigger={pettyCashRefreshTrigger}
        />
      )}
      {/* Import History Modal */}
      <ImportHistoryModal
        isOpen={showImportHistoryModal}
        onClose={() => setShowImportHistoryModal(false)}
      />
      {/* Bulk Categorize Modal */}
      {showBulkCategorizeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Bulk Categorize Transactions</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Select a category for {bulkCategorizeTransactions?.length} selected transaction{bulkCategorizeTransactions?.length > 1 ? 's' : ''}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowBulkCategorizeModal(false);
                  setBulkCategorizeTransactions([]);
                }}
                iconName="X"
              />
            </div>

            <div className="p-6 space-y-4">
              {/* Category Selection Grid */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'office-supplies', label: 'Office Supplies', icon: '📎' },
                  { value: 'marketing', label: 'Marketing', icon: '📢' },
                  { value: 'travel', label: 'Travel', icon: '✈️' },
                  { value: 'utilities', label: 'Utilities', icon: '💡' },
                  { value: 'professional-services', label: 'Professional Services', icon: '👔' },
                  { value: 'equipment', label: 'Equipment', icon: '🔧' },
                  { value: 'revenue', label: 'Revenue', icon: '💰' },
                  { value: 'transfer', label: 'Transfer', icon: '🔄' },
                  { value: 'other', label: 'Other', icon: '📋' }
                ]?.map(category => (
                  <button
                    key={category?.value}
                    onClick={() => handleBulkCategorize(category?.value)}
                    className="flex items-center space-x-2 p-3 text-left border border-border rounded-lg hover:bg-muted/20 transition-colors"
                  >
                    <span className="text-lg">{category?.icon}</span>
                    <span className="text-sm font-medium text-foreground">{category?.label}</span>
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkCategorizeModal(false);
                    setBulkCategorizeTransactions([]);
                  }}
                >
                  Cancel
                </Button>
                <p className="text-sm text-muted-foreground">
                  Click a category to apply to all selected transactions
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Add Category Manager Modal before closing div */}
      <TransactionCategoryManager
        isOpen={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        organizationId={userProfile?.organizations?.[0]?.id}
      />
    </div>
  );
};

export default Banking;